/**
 * Merged cart.js (Cart Page / Online Store 2.0)
 * - Mystery Box promo: adds random gift variants when product 7936843776074 is increased
 *   -> Uses a sessionStorage-backed pool to avoid duplicates until pool exhausted
 * - Kickoff promo: manages a specific gift variant (42635282350154) based on cart contents
 * - Unified cart-items, cart-remove-button, cart-note (no duplicate customElements)
 *
 * Assumptions:
 * - Theme provides: routes (cart_url, cart_change_url, cart_update_url), fetchConfig(),
 *   publish(), subscribe(), PUB_SUB_EVENTS, trapFocus(), window.cartStrings
 */

(() => {
    const MYSTERY_TARGET_PRODUCT_ID = [8079491334218, 7497438822474];
    const MYSTERY_TARGET_VARIANT_IDS = [42266736656458, 42266736689226, 43121277632586, 43121277665354, 43121303617610];
  
  // Gift variant IDs - all 13 mystery box variants (random selection)
  const MYSTERY_GIFT_VARIANT_IDS = [
      43122416123978,
      43122416189514,
      43175788380234,
      43175788544074,
      43175788576842,
      43175788642378,
      43250325160010,
      43250325192778,
      
  ];

  // Key for sessionStorage
  const MYSTERY_POOL_KEY = 'giftVariantPool';
  const BOGO_MYSTERY_PRODUCT_ID = 8093507649610;
  const BOGO_MYSTERY_GIFT_VARIANT_IDS = [
    43184708386890,
    43184708452426,
    43250323259466,
    43389539745866,
    43250323947594,
    43400104181834,
    43400104214602
  ];
  const BOGO_MYSTERY_POOL_KEY = 'bogoMysteryPool';

  let isNormalizingBogoLines = false;

  async function normalizeBogoProductLines(cartData) {
    if (isNormalizingBogoLines) return cartData;
    if (!cartData || !Array.isArray(cartData.items)) return cartData;

    const itemsNeedingSplit = cartData.items.filter(
      (item) => isBogoMysteryItem(item) && Number(item.quantity) > 1
    );

    if (!itemsNeedingSplit.length) return cartData;

    isNormalizingBogoLines = true;

    try {
      // Track ALL variants currently in cart (purchased + gifts)
      const allInCartVariants = new Set();
      cartData.items.forEach((item) => {
        const itemId = parseInt(item.id);
        if (isBogoMysteryItem(item)) {
          allInCartVariants.add(itemId);
        }
        if (BOGO_MYSTERY_GIFT_VARIANT_IDS.includes(itemId)) {
          allInCartVariants.add(itemId);
        }
      });

      // Load pool and remove anything already in cart
      let storedPool = loadBogoMysteryPool();
      if (!Array.isArray(storedPool)) storedPool = [];
      
      // Filter pool to exclude all in-cart variants
      storedPool = storedPool.filter((id) => !allInCartVariants.has(id));

      // If pool is empty, regenerate excluding all in-cart variants
      if (!storedPool.length) {
        storedPool = regenBogoPool(Array.from(allInCartVariants));
      }

      const takeNextVariant = () => {
        // Filter pool again to ensure no in-cart variants
        storedPool = storedPool.filter((id) => !allInCartVariants.has(id));
        
        // If empty, regenerate
        if (!storedPool.length) {
          storedPool = regenBogoPool(Array.from(allInCartVariants));
        }

        // Take from the end
        const next = storedPool.pop();
        if (typeof next === 'undefined') return null;
        
        // Mark as used
        allInCartVariants.add(next);

        return next;
      };

      for (const item of itemsNeedingSplit) {
        const currentQty = Number(item.quantity) || 0;
        const extraQty = currentQty - 1;
        if (extraQty <= 0) continue;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 1 }),
        });

        for (let i = 0; i < extraQty; i++) {
          const variantId = takeNextVariant();
          if (!variantId) continue;

          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
        }
      }

      // Save the remaining pool (already filtered)
      const poolToSave = storedPool.length ? storedPool : regenBogoPool(Array.from(allInCartVariants));
      saveBogoMysteryPool(poolToSave);

      // Small delay to ensure Shopify backend is synced
      await sleep(100);
      
      // Fetch fresh cart data
      const freshCart = await fetch('/cart.js').then((r) => r.json());
      
      // Immediately refresh UI to show the split variants
      const drawerEl = document.querySelector('cart-drawer');
      const isCartPage = window.location.pathname === '/cart' || window.location.pathname === routes.cart_url;
      if (drawerEl && !isCartPage) {
        await refreshCartDrawerSections();
      } else {
        await refreshCartSection();
      }
      
      return freshCart;
    } catch (err) {
      console.error('normalizeBogoProductLines error:', err);
      return cartData;
    } finally {
      isNormalizingBogoLines = false;
    }
  }

  // Fisher-Yates shuffle to randomize order
  function shuffleIds(ids) {
    const arr = ids.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function loadBogoMysteryPool() {
    try {
      const raw = sessionStorage.getItem(BOGO_MYSTERY_POOL_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.slice();
        }
      }
    } catch (e) {}
    const freshPool = regenBogoPool();
    saveBogoMysteryPool(freshPool);
    return freshPool.slice();
  }

  function saveBogoMysteryPool(pool) {
    try {
      sessionStorage.setItem(BOGO_MYSTERY_POOL_KEY, JSON.stringify(pool));
    } catch (e) {}
  }

  function regenBogoPool(excludeIds = []) {
    const fresh = BOGO_MYSTERY_GIFT_VARIANT_IDS.filter((id) => !excludeIds.includes(id));
    return shuffleIds(fresh.length ? fresh : BOGO_MYSTERY_GIFT_VARIANT_IDS);
  }

  function isBogoMysteryItem(item) {
    if (!item) return false;
    const productId = parseInt(item.product_id);
    return productId === BOGO_MYSTERY_PRODUCT_ID;
  }

  function normalizeToIntArray(value) {
    if (Array.isArray(value)) return value.map((v) => parseInt(v)).filter((n) => Number.isFinite(n));
    const n = parseInt(value);
    return Number.isFinite(n) ? [n] : [];
  }

  function isMysteryTargetItem(item) {
    if (!item) return false;

    const variantId = parseInt(item.id || item.variant_id);
    const productId = parseInt(item.product_id);

    const targetVariantIds = normalizeToIntArray(MYSTERY_TARGET_VARIANT_IDS);
    const targetProductIds = normalizeToIntArray(MYSTERY_TARGET_PRODUCT_ID);

    const matchesVariant = Number.isFinite(variantId) && targetVariantIds.includes(variantId);
    const matchesProduct = Number.isFinite(productId) && targetProductIds.includes(productId);

    return matchesVariant || matchesProduct;
  }

  const KICKOFF_GIFT_VARIANT_ID = 426352823501540000; // fixed kickoff gift variant
// const KICKOFF_GIFT_VARIANT_ID = 123456789;
  const ON_CHANGE_DEBOUNCE_TIMER = 300;

  /* ============================
     UTILITIES
     ============================ */
  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function sleep(ms) {
    return new Promise((res) => setTimeout(res, ms));
  }

  // Safe CSS.escape fallback if not available
  function escapeForSelector(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return s.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|\/@])/g, '\\$1');
  }

  /* ============================
     MYSTERY POOL (module-level)
     - Keep a single in-memory pool and keep in sync with sessionStorage.
     - This prevents duplication across quick successive calls.
     ============================ */
  let mysteryPool = null;

  function loadMysteryPool() {
    if (mysteryPool) return mysteryPool;

    try {
      const raw = sessionStorage.getItem(MYSTERY_POOL_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          mysteryPool = parsed.slice();
          return mysteryPool;
        }
      }
    } catch (e) {}
    mysteryPool = MYSTERY_GIFT_VARIANT_IDS.slice();
    saveMysteryPool();
    return mysteryPool;
  }

  function saveMysteryPool() {
    try {
      sessionStorage.setItem(MYSTERY_POOL_KEY, JSON.stringify(mysteryPool));
    } catch (e) {}
  }

  function getNextMysteryVariant() {
    loadMysteryPool();
    if (!Array.isArray(mysteryPool) || mysteryPool.length === 0) {
      mysteryPool = MYSTERY_GIFT_VARIANT_IDS.slice();
    }
    const idx = Math.floor(Math.random() * mysteryPool.length);
    const selected = mysteryPool.splice(idx, 1)[0];
    saveMysteryPool();
    return selected;
  }

  // Optional helper to reset the pool (exposed later)
  function resetMysteryPool() {
    mysteryPool = MYSTERY_GIFT_VARIANT_IDS.slice();
    saveMysteryPool();
  }

  /* ============================
     LOADER HELPERS
     ============================ */
  function toggleMysteryGiftDrawerStatus(isLoading, attempt = 0) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      if (isLoading && attempt < 20) setTimeout(() => toggleMysteryGiftDrawerStatus(isLoading, attempt + 1), 50);
      return;
    }

    const el = drawer.querySelector('#mystery-gift-drawer-overlay');
    if (!el) {
      if (isLoading && attempt < 20) setTimeout(() => toggleMysteryGiftDrawerStatus(isLoading, attempt + 1), 50);
      return;
    }

    el.classList.toggle('hidden', !isLoading);
    el.setAttribute('aria-hidden', (!isLoading).toString());
  }

  function toggleDrawerGiftLoader(isLoading, attempt = 0) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      // Drawer may not be in DOM yet (product pages); retry briefly.
      if (isLoading && attempt < 20) setTimeout(() => toggleDrawerGiftLoader(isLoading, attempt + 1), 50);
      return;
    }

    const header = drawer.querySelector('.drawer__header');
    if (!header) {
      if (isLoading && attempt < 20) setTimeout(() => toggleDrawerGiftLoader(isLoading, attempt + 1), 50);
      return;
    }

    let loader = drawer.querySelector('#mystery-gift-drawer-loader');
    if (!loader) {
      try {
        const headerPos = window.getComputedStyle(header).position;
        if (headerPos === 'static') header.style.position = 'relative';
      } catch (e) {}

      loader = document.createElement('div');
      loader.id = 'mystery-gift-drawer-loader';
      loader.className = 'loading-overlay hidden';
      loader.style.left = 'auto';
      loader.style.right = '12px';
      loader.style.top = '12px';
      loader.style.paddingTop = '0';
      loader.innerHTML = `
        <div class="loading-overlay__spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;
      header.appendChild(loader);
    }

    loader.classList.toggle('hidden', !isLoading);
  }

  function showCartLoader() {
    const loader = document.getElementById('custom-loader-cart');
    if (loader) loader.classList.remove('d-none');
  }
  function hideCartLoader() {
    const loader = document.getElementById('custom-loader-cart');
    if (loader) loader.classList.add('d-none');
  }

  /* ============================
     USER-REMOVED FLAG (kickoff)
     - kept on window so other scripts can modify if needed
     ============================ */
  if (typeof window.userRemovedGift === 'undefined') window.userRemovedGift = false;

  /* ============================
     CUSTOM ELEMENTS: cart-remove-button
     (guard against duplicate definitions)
     ============================ */
  if (!customElements.get('cart-remove-button')) {
    class CartRemoveButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          // show loader then perform default behavior
          showCartLoader();
          event.preventDefault();
          const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
          cartItems.updateQuantity(this.dataset.key, this.dataset.index, 0);
        });
      }
    }
    customElements.define('cart-remove-button', CartRemoveButton);
  }

  /* ============================
     Unified cart-items (merges both scripts' behaviors)
     ============================ */
  if (!customElements.get('cart-items')) {
    class CartItems extends HTMLElement {
      constructor() {
        super();

        this.lineItemStatusElement =
          document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

        const debouncedOnChange = debounce((event) => {
          this.onChange(event);
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));

        // ensure pool is loaded into memory
        loadMysteryPool();
      }

      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        // subscribe to pub/sub (if available)
        try {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'cart-items') return;
            this.onCartUpdate();
          });
        } catch (e) {
          // subscribe might not be present in some themes; ignore
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        // Pass along dataset values (productId may be on some templates)
        this.updateQuantity(
          event.target.dataset.key,
          event.target.dataset.index,
          event.target.value,
          document.activeElement.getAttribute('name'),
          event.target.dataset.quantityVariantId,
          event.target.dataset.productId
        );
        
          showCartLoader();
      }

      onCartUpdate() {
        // Re-fetch cart sections depending on whether this is drawer or main cart.
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          fetch(`${routes.cart_url}?section_id=cart-drawer`)
            .then((r) => r.text())
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
              selectors.forEach((selector) => {
                const target = document.querySelector(selector);
                const source = html.querySelector(selector);
                if (target && source) target.replaceWith(source);
              });
            })
            .catch((e) => console.error(e));
        } else {
          // cart page main items
          fetch(`${routes.cart_url}?section_id=main-cart-items`)
            .then((r) => r.text())
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const source = html.querySelector('cart-items');
              if (source) this.innerHTML = source.innerHTML;
            })
            .catch((e) => console.error(e));
        }
      }

      getSectionsToRender() {
        return [
          {
            id: 'main-cart-items',
            section: document.getElementById('main-cart-items').dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'cart-live-region-text',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'main-cart-footer',
            section: document.getElementById('main-cart-footer').dataset.id,
            selector: '.js-contents',
          },
        ];
      }

      /**
       * updateQuantity - main entry point for changes from inputs and remove buttons
       * - Just do normal cart updates, let autoAddMysteryGifts handle gift logic via cart update event
       */
      updateQuantity(key, line, quantity, name, variantId, productId) {
        const itemKeyParts = key.split(':');
        itemKeyParts.splice(1, 0, '\\:');
        const formattedKey = itemKeyParts.join('');

        this.enableLoading(formattedKey);

        const quantityElement =
          document.getElementById(`Quantity-${key}`) || document.getElementById(`Drawer-quantity-${key}`);

        // Always do normal update - autoAddMysteryGifts will handle gift management
        this.performNormalUpdate(key, quantity, quantityElement, formattedKey, name, variantId);
      }

      // Helper method to refresh cart drawer sections
      async refreshCartDrawerSections() {
        // Use the global function instead
        await refreshCartDrawerSections();
      }




      performNormalUpdate(key, quantity, quantityElement, formattedKey, name, variantId) {
        const body = JSON.stringify({
          id: key,
          quantity,
          sections: this.getSectionsToRender().map((s) => s.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsed = JSON.parse(state);

            // If quantityElement missing, attempt to find again
            if (!quantityElement) {
              quantityElement = document.getElementById(`Quantity-${key}`) || document.getElementById(`Drawer-quantity-${key}`);
            }

            if (parsed.errors) {
              if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
              this.updateLiveRegions(key, parsed.errors);
              return;
            }

            // SavedBy vendor filter (preserve original logic)
            const SavedBy = parsed.items.find((item) => item.vendor === 'SavedBy');
            let itemCount = parsed.item_count;
            let itemLength = parsed.items.length;
            if (SavedBy) {
              itemCount -= 1;
              itemLength -= 1;
            }

            // Toggle empty classes
            this.classList.toggle('is-empty', itemCount === 0);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            const cartFooter = document.getElementById('main-cart-footer');

            if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
            if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', itemCount === 0);

            // Update sections
            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsed.sections[section.section], section.selector);
            });

            // Quantity validation messaging
            const updatedItem = parsed.items.find((item) => item.key === key);
            const updatedValue = updatedItem ? updatedItem.quantity : undefined;
            const items = document.querySelectorAll('.cart-item');

            let message = '';
            if (items.length === itemLength && updatedValue !== (quantityElement ? parseInt(quantityElement.value) : undefined)) {
              if (typeof updatedValue === 'undefined') {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
              }
            }
            this.updateLiveRegions(key, message);

            // Focus management (trap focus if drawer)
            const lineItem = document.getElementById(`CartItem-${key}`) || document.getElementById(`CartDrawer-Item-${key}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (itemCount === 0 && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
            } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
            }

            // Publish event for kickoff / other listeners
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsed, variantId });
          })
          .catch((err) => {
            console.error('Cart update error', err);
            this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
            const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
            if (errors) errors.textContent = window.cartStrings.error;
          })
          .finally(() => {
            this.disableLoading(formattedKey);
          });
      }

      updateLiveRegions(key, message) {
        const lineItemError =
          document.getElementById(`Line-item-error-${key}`) || document.getElementById(`CartDrawer-LineItemError-${key}`);
        if (lineItemError) {
          const el = lineItemError.querySelector('.cart-item__error-text');
          if (el) el.innerHTML = message;
        }

        if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        if (cartStatus) {
          cartStatus.setAttribute('aria-hidden', false);
          setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
        }
      }

      getSectionInnerHTML(html, selector) {
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : '';
      }

      enableLoading(key) {
        const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

        const cartItemElements = this.querySelectorAll(`#CartItem-${key} .loading-overlay`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${key} .loading-overlay`);
        [...cartItemElements, ...cartDrawerItemElements].forEach((ov) => ov.classList.remove('hidden'));

        try {
          document.activeElement.blur();
        } catch (e) {}

        if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
      }

      disableLoading(key) {
        const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

        const cartItemElements = this.querySelectorAll(`#CartItem-${key} .loading-overlay`);
        const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${key} .loading-overlay`);
        cartItemElements.forEach((ov) => ov.classList.add('hidden'));
        cartDrawerItemElements.forEach((ov) => ov.classList.add('hidden'));
      }
    }

    customElements.define('cart-items', CartItems);
  }

  // NOTE: cart-drawer-items is defined in assets/cart-drawer.js (it extends cart-items
  // and overrides getSectionsToRender for the drawer). Do not define it here.

  /* ============================
     cart-note element (guarded)
     ============================ */
  if (!customElements.get('cart-note')) {
    customElements.define(
      'cart-note',
      class CartNote extends HTMLElement {
        constructor() {
          super();
          this.addEventListener(
            'change',
            debounce((event) => {
              const body = JSON.stringify({ note: event.target.value });
              fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
            }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }
      }
    );
  }

  /* ============================
     KICKOFF PROMO LOGIC
     - Runs on initial load + on cart updates
     ============================ */

  // Add/remove/correct kickoff gift based on cart contents
  async function handleKickoffGift(cartData = null) {
    showCartLoader();
    try {
      const data = cartData || (await fetch('/cart.js').then((r) => r.json()));
      const hasEligibleItem = data.items.some(
  (item) => {
    const isEligible =
      (item.variant_title && item.variant_title.trim().startsWith('Non-Prescription')) ||
      (item.title && item.title.includes('Reading')) ||
      (item.title && item.title.includes('Sunglasses'));

    // Check if this eligible item's price (for one unit) is >= 40
    // Shopify returns price in cents
    const itemPrice = item.price || 0;
    return isEligible && (itemPrice / 100) >= 41;
  }
);

// console.log('Eligible item exists with price >= $41:', hasEligibleItem);
      // const hasEligibleItem = data.items.some(
      //   (item) =>
      //     (item.variant_title && item.variant_title.trim().startsWith('Non-Prescription')) ||
      //     (item.title && item.title.includes('Reading')) ||
      //     (item.title && item.title.includes('Sunglasses'))
      // );

      const giftItem = data.items.find((item) => item.id === KICKOFF_GIFT_VARIANT_ID);

      // If user removed gift manually, do not re-add until they requalify
      if (window.userRemovedGift && !giftItem) {
        if (!hasEligibleItem) window.userRemovedGift = false; // reset when user removes qualifying item
        return;
      }

      if (hasEligibleItem && !giftItem) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: KICKOFF_GIFT_VARIANT_ID, quantity: 1 }] }),
        });
        await refreshCartSection();
      } else if (hasEligibleItem && giftItem && giftItem.quantity !== 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 1 }),
        });
        await refreshCartSection();
      } else if (!hasEligibleItem && giftItem) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
        });
        await refreshCartSection();
      }
    } catch (err) {
      console.error('Kickoff gift logic error:', err);
    } finally {
      hideCartLoader();
    }
  }

  // Utility to refresh cart DOM sections (cart page)
  async function refreshCartSection() {
    try {
      const res = await fetch(window.location.pathname);
      if (!res.ok) return;
      const html = await res.text();
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');

      const newCartItems = newDoc.querySelector('cart-items');
      const newFooter = newDoc.querySelector('#cart-footer');
      const newMainFooter = newDoc.querySelector('#main-cart-footer');

      if (newCartItems && document.querySelector('cart-items')) document.querySelector('cart-items').replaceWith(newCartItems);
      if (newFooter && document.querySelector('#cart-footer')) document.querySelector('#cart-footer').replaceWith(newFooter);
      if (newMainFooter && document.querySelector('#main-cart-footer')) document.querySelector('#main-cart-footer').replaceWith(newMainFooter);
    } catch (e) {
      console.error('refreshCartSection error', e);
    }
  }

  // Called when user explicitly removes the kickoff gift via UI
  function markKickoffGiftRemoved() {
    window.userRemovedGift = true;
  }

  // Click handler for .remove-gift-btn (kickoff original custom button)
  document.addEventListener('click', async (e) => {
    const t = e.target;
    if (!t) return;
    if (t.classList.contains('remove-gift-btn')) {
      e.preventDefault();
      const key = t.getAttribute('data-key');
      if (!key) return;
      t.textContent = 'Removing...';
      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        const cart = await resp.json();
        window.userRemovedGift = true;

        // remove DOM row if present
        const escapedKey = escapeForSelector(key);
        const itemRow =
          document.querySelector(`#CartItem-${escapedKey}`) ||
          document.querySelector(`#CartDrawer-Item-${escapedKey}`) ||
          document.querySelector(`[data-key="${key}"]`);
        if (itemRow) itemRow.remove();

        // remove container containing .kick_off if present
        const kickOffDiv = document.querySelector('.kick_off');
        if (kickOffDiv) kickOffDiv.remove();

        // update count bubbles
        const countEls = document.querySelectorAll('.cart-count, .cart-count-bubble');
        countEls.forEach((el) => (el.textContent = cart.item_count));

        t.textContent = 'Removed';
      } catch (err) {
        console.error('Remove gift failed:', err);
        t.textContent = 'Try Again';
      }
    }
  });

  // Lock gift UI (disable direct quantity changes for kickoff gift and attach remove listeners)
  function lockKickoffGiftUI() {
    const giftRow =
      document.querySelector(`[data-variant-id="${KICKOFF_GIFT_VARIANT_ID}"]`) ||
      document.querySelector(`[data-key*="${KICKOFF_GIFT_VARIANT_ID}"]`);
    if (!giftRow) return;

    const qtyInput = giftRow.querySelector('input[name="updates[]"], .quantity__input');
    const removeBtn = giftRow.querySelector('cart-remove-button, .cart-remove-button');

    if (qtyInput) {
      qtyInput.disabled = true;
      qtyInput.value = 1;
    }

    if (removeBtn) {
      removeBtn.style.display = 'inline-flex';
      removeBtn.disabled = false;
      removeBtn.removeEventListener('click', markKickoffGiftRemoved);
      removeBtn.addEventListener('click', markKickoffGiftRemoved);
    }
  }

  const kickoffObserver = new MutationObserver(() => lockKickoffGiftUI());

  /* ============================
     AUTO-ADD/REMOVE MYSTERY GIFTS ON CART UPDATE
     - When MYSTERY_TARGET_PRODUCT_ID is added, automatically add random mystery gift (BOGO style)
     - When target is removed/decreased, remove corresponding gifts
     ============================ */
  async function autoAddMysteryGifts(cartData, opts = {}) {
    const attempt = Number.isFinite(opts.attempt) ? opts.attempt : 0;
    try {
      if (!cartData || !cartData.items) return;

      // Find the mystery target items in cart (may be multiple variants)
      const targetItems = cartData.items.filter((item) => isMysteryTargetItem(item));
      const targetQty = targetItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      // Load pool
      loadMysteryPool();

      // Get all gift variants already in cart
      const giftItemsInCart = cartData.items.filter(item => MYSTERY_GIFT_VARIANT_IDS.includes(item.id));
      const totalGiftsInCart = giftItemsInCart.reduce((sum, item) => sum + (item.quantity || 0), 0);

      // Calculate difference (BOGO: 1 target = 1 gift)
      const difference = targetQty - totalGiftsInCart;
      
      if (difference === 0) return; // Perfect balance, nothing to do

      // Show overlay only when eligible item needs gift ADD
      if (difference > 0 && targetQty > 0) {
        toggleMysteryGiftDrawerStatus(true);
      }

      showCartLoader();

      // If we need to ADD gifts (difference > 0)
      if (difference > 0) {
        // Get used gift IDs to avoid duplicates
        const usedGiftsInCart = giftItemsInCart.map((item) => item.id);
        mysteryPool = mysteryPool.filter((id) => !usedGiftsInCart.includes(id));

        if (mysteryPool.length === 0) {
          mysteryPool = MYSTERY_GIFT_VARIANT_IDS.slice();
        }

        const addedInThisBatch = [];
        const variantsToAdd = [];

        for (let i = 0; i < difference; i++) {
          let availablePool = mysteryPool.filter((id) => !addedInThisBatch.includes(id));
          if (availablePool.length === 0) {
            availablePool = MYSTERY_GIFT_VARIANT_IDS.slice().filter((id) => !addedInThisBatch.includes(id));
            if (availablePool.length === 0) {
              availablePool = MYSTERY_GIFT_VARIANT_IDS.slice();
            }
          }

          const idx = Math.floor(Math.random() * availablePool.length);
          const giftVariantId = availablePool[idx];
          variantsToAdd.push(giftVariantId);

          const poolIdx = mysteryPool.indexOf(giftVariantId);
          if (poolIdx > -1) mysteryPool.splice(poolIdx, 1);

          addedInThisBatch.push(giftVariantId);
        }

        saveMysteryPool();

        // Batch add requests for speed
        await Promise.all(
          variantsToAdd.map((giftVariantId) =>
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: giftVariantId, quantity: 1 }),
            })
          )
        );
      }
      // If we need to REMOVE gifts (difference < 0)
      else if (difference < 0) {
        const giftsToRemove = Math.abs(difference);
        const giftsToRemoveKeys = giftItemsInCart.slice(0, giftsToRemove).map((item) => item.key);

        // Batch remove requests for speed
        await Promise.all(
          giftsToRemoveKeys.map((giftKey) =>
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: giftKey, quantity: 0 }),
            })
          )
        );
      }

      // Refresh appropriate sections based on context
      // On product/collection pages we want the drawer to reflect changes.
      const drawerEl = document.querySelector('cart-drawer');
      const isCartPage = window.location.pathname === '/cart' || window.location.pathname === routes.cart_url;
      if (drawerEl && !isCartPage) {
        await refreshCartDrawerSections();
      } else {
        await refreshCartSection();
      }

      // Reconcile once/twice to avoid edge-cases where Shopify returns slightly stale state
      // (prevents "one gift left" when many ops happen quickly).
      if (attempt < 2) {
        const fresh = await fetch('/cart.js').then((r) => r.json());
        const freshTargetItems = fresh.items.filter((item) => isMysteryTargetItem(item));
        const freshTargetQty = freshTargetItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const freshGiftItems = fresh.items.filter((item) => MYSTERY_GIFT_VARIANT_IDS.includes(item.id));
        const freshTotalGifts = freshGiftItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const freshDiff = freshTargetQty - freshTotalGifts;

        if (freshDiff !== 0) {
          await autoAddMysteryGifts(fresh, { attempt: attempt + 1 });
        }
      }

    } catch (err) {
      console.error('autoAddMysteryGifts error:', err);
    } finally {
      hideCartLoader();
      toggleMysteryGiftDrawerStatus(false);
    }
  }

  // Helper to refresh cart drawer sections
  async function refreshCartDrawerSections() {
    try {
      const [drawerRes, bubbleRes] = await Promise.all([
        fetch(`${routes.cart_url}?section_id=cart-drawer`),
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`),
      ]);

      const [html, bubbleHtml] = await Promise.all([drawerRes.text(), bubbleRes.text()]);
      const newDoc = new DOMParser().parseFromString(html, 'text/html');
      
      // Update cart drawer items content
      const newCartDrawerItems = newDoc.querySelector('cart-drawer-items');
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');
      if (newCartDrawerItems && currentCartDrawerItems) {
        currentCartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
      }
      
      // Update the drawer footer
      const newDrawerFooter = newDoc.querySelector('.drawer__footer');
      const currentDrawerFooter = document.querySelector('.drawer__footer');
      if (newDrawerFooter && currentDrawerFooter) {
        currentDrawerFooter.innerHTML = newDrawerFooter.innerHTML;
      }
      
      // Update drawer header
      const newDrawerHeader = newDoc.querySelector('.drawer__header');
      const currentDrawerHeader = document.querySelector('.drawer__header');
      if (newDrawerHeader && currentDrawerHeader) {
        currentDrawerHeader.innerHTML = newDrawerHeader.innerHTML;
      }

      // Update cart icon bubble
      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const newBubble = bubbleDoc.querySelector('.shopify-section');
      const oldBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && oldBubble) {
        oldBubble.innerHTML = newBubble.innerHTML;
      }

      // Update drawer empty state
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const cart = await fetch('/cart.js').then(r => r.json());
        const savedBy = cart.items.find(i => i.vendor === 'SavedBy');
        const itemCount = savedBy ? cart.item_count - 1 : cart.item_count;
        cartDrawer.classList.toggle('is-empty', itemCount === 0);
        
        if (currentCartDrawerItems) {
          currentCartDrawerItems.classList.toggle('is-empty', itemCount === 0);
        }
      }
    } catch (err) {
      console.error('refreshCartDrawerSections error:', err);
    }
  }

  /* ============================
     BOGO MYSTERY BOX LOGIC
     - Handles product 8093507649610 with Buy 1 Get 1 at 50% off
     - Adds random gift variants from BOGO_MYSTERY_GIFT_VARIANT_IDS
     ============================ */
  async function autoAddBogoMysteryGifts(cartData, opts = {}) {
    const attempt = Number.isFinite(opts.attempt) ? opts.attempt : 0;
    try {
      // Show loader at the very start
      showCartLoader();
      
      if (isNormalizingBogoLines) {
        for (let i = 0; i < 10 && isNormalizingBogoLines; i++) {
          await sleep(75);
        }
      }

      // Always fetch fresh cart data to avoid stale state
      let freshCartData = await fetch('/cart.js').then((r) => r.json());
      freshCartData = await normalizeBogoProductLines(freshCartData);

      if (!freshCartData || !freshCartData.items) return;

      // Find BOGO mystery box items in cart
      const bogoItems = freshCartData.items.filter((item) => isBogoMysteryItem(item));
      const bogoQty = bogoItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      // Always load fresh pool from storage
      let bogoPool = loadBogoMysteryPool();

      // Get all BOGO gift variants already in cart (with fresh data)
      const bogoGiftItemsInCart = freshCartData.items.filter(item => BOGO_MYSTERY_GIFT_VARIANT_IDS.includes(item.id));
      const totalBogoGiftsInCart = bogoGiftItemsInCart.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const usedBogoGiftsInCart = bogoGiftItemsInCart.map((item) => item.id);
      const usedBogoProductVariants = bogoItems.map((item) => item.id);

      // Calculate difference (BOGO: 1 purchased = 1 gift at 50% off)
      const difference = bogoQty - totalBogoGiftsInCart;
      
      if (difference === 0) return; // Perfect balance

      // Show overlay when adding gifts
      if (difference > 0 && bogoQty > 0) {
        toggleMysteryGiftDrawerStatus(true);
      }

      // If we need to ADD gifts
      if (difference > 0) {
        // Create set of ALL variants currently in cart
        const allInCartVariants = new Set([...usedBogoGiftsInCart, ...usedBogoProductVariants]);

        // Filter persisted pool to exclude ALL in-cart variants
        bogoPool = Array.isArray(bogoPool) ? bogoPool.filter((id) => !allInCartVariants.has(id)) : [];

        // If pool is empty or insufficient, regenerate excluding all in-cart variants
        if (bogoPool.length === 0) {
          bogoPool = regenBogoPool(Array.from(allInCartVariants));
        }

        const variantsToAdd = [];
        // Track all used variants in this cycle (cart + batch)
        const usedInCycle = new Set(allInCartVariants);

        for (let i = 0; i < difference; i++) {
          // Filter pool to exclude variants used in this cycle
          bogoPool = bogoPool.filter((id) => !usedInCycle.has(id));
          
          // If pool is empty, start a new cycle
          if (bogoPool.length === 0) {
            // Reset cycle tracking - all variants have been used once
            usedInCycle.clear();
            // Add back only the variants added in THIS batch (not cart items) to avoid immediate repeats
            variantsToAdd.forEach(id => usedInCycle.add(id));
            
            // Regenerate full pool excluding what we just added in this batch
            bogoPool = shuffleIds(BOGO_MYSTERY_GIFT_VARIANT_IDS.filter(id => !usedInCycle.has(id)));
            
            // If still empty (batch used all variants), allow full pool
            if (bogoPool.length === 0) {
              bogoPool = shuffleIds(BOGO_MYSTERY_GIFT_VARIANT_IDS.slice());
            }
          }

          // Take from the end (already shuffled)
          const giftVariantId = bogoPool.pop();
          if (typeof giftVariantId === 'undefined') continue;
          
          variantsToAdd.push(giftVariantId);
          // Mark as used in this cycle
          usedInCycle.add(giftVariantId);
        }

        // Save remaining pool to storage
        const poolToSave = bogoPool.length ? bogoPool : regenBogoPool(Array.from(allInCartVariants));
        saveBogoMysteryPool(poolToSave);

        // Batch add requests
        await Promise.all(
          variantsToAdd.map((giftVariantId) =>
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: giftVariantId, quantity: 1 }),
            })
          )
        );
      }
      // If we need to REMOVE gifts
      else if (difference < 0) {
        const giftsToRemove = Math.abs(difference);
        const giftsToRemoveKeys = bogoGiftItemsInCart.slice(0, giftsToRemove).map((item) => item.key);

        await Promise.all(
          giftsToRemoveKeys.map((key) =>
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            })
          )
        );
      }

      // Small delay to ensure all backend operations complete
      await sleep(150);
      
      // Refresh sections to show final state
      const drawerEl = document.querySelector('cart-drawer');
      const isCartPage = window.location.pathname === '/cart' || window.location.pathname === routes.cart_url;
      if (drawerEl && !isCartPage) {
        await refreshCartDrawerSections();
      } else {
        await refreshCartSection();
      }
      
      // Small delay after refresh to ensure DOM updates complete
      await sleep(100);

      // Reconcile to ensure accuracy
      if (attempt < 2) {
        const fresh = await fetch('/cart.js').then((r) => r.json());
        const freshBogoItems = fresh.items.filter((item) => isBogoMysteryItem(item));
        const freshBogoQty = freshBogoItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const freshBogoGiftItems = fresh.items.filter((item) => BOGO_MYSTERY_GIFT_VARIANT_IDS.includes(item.id));
        const freshTotalBogoGifts = freshBogoGiftItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const freshDiff = freshBogoQty - freshTotalBogoGifts;

        if (freshDiff !== 0) {
          await autoAddBogoMysteryGifts(fresh, { attempt: attempt + 1 });
        }
      }

    } catch (err) {
      console.error('autoAddBogoMysteryGifts error:', err);
    } finally {
      hideCartLoader();
      toggleMysteryGiftDrawerStatus(false);
    }
  }

  /* ============================
     INITIALIZATION
     ============================ */
  document.addEventListener('DOMContentLoaded', async () => {
    // Run kickoff logic once at load
    try {
      await handleKickoffGift();
    } catch (e) {
      console.error('Initial kickoff run error', e);
    }

    // Run mystery gift sync on page load (handles add to cart from product page)
    try {
      const cartData = await fetch('/cart.js').then(r => r.json());
      await autoAddMysteryGifts(cartData);
      await autoAddBogoMysteryGifts(cartData);
    } catch (e) {
      console.error('Initial mystery gift sync error', e);
    }

    // Start observer to lock kickoff gift UI
    try {
      kickoffObserver.observe(document.body, { childList: true, subtree: true });
      lockKickoffGiftUI();
    } catch (e) {
      // ignore observer errors
    }
  });

  // Subscribe to cart updates so kickoff logic runs when cart changes
  try {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      // Allow kickoff to receive the cart data if provided
      try {
        await handleKickoffGift(event?.cartData);
      } catch (e) {
        console.error('kickoff handler on cartUpdate failed', e);
      }
      
      // Auto-add mystery gifts if target product is in cart
      try {
        const cartData = event?.cartData || (await fetch('/cart.js').then((r) => r.json()));
        await autoAddMysteryGifts(cartData);
        await autoAddBogoMysteryGifts(cartData);
      } catch (e) {
        console.error('autoAddMysteryGifts handler failed', e);
      }
      
      // Re-lock UI after updates
      lockKickoffGiftUI();
    });
  } catch (e) {
    // subscribe not available — ignore
  }

  /* ============================
     EXPOSURES: debug helpers
     ============================ */
  window.MergedCartHelpers = {
    resetMysteryPool,
    checkMysteryPool: () => {
      loadMysteryPool();
      console.info('Mystery pool:', mysteryPool);
    },
    markKickoffGiftRemoved,
    checkBogoMysteryPool: () => {
      const pool = loadBogoMysteryPool();
      console.info('BOGO Mystery pool:', pool);
    },
    resetBogoMysteryPool: () => {
      saveBogoMysteryPool(BOGO_MYSTERY_GIFT_VARIANT_IDS.slice());
      console.info('BOGO Mystery pool reset');
    }
  };
})();
